import { useEffect, useMemo, useState } from 'react';
import adminApi from '@/lib/adminApi';
import { analyzeArtistProfile, analyzeCollaboratorProfile } from '@/lib/profileCoach';

const initialForm = {
  audience: 'all_artists',
  channels: ['email'],
  recipient_ids: [],
  subject: '',
  message: '',
  verified_only: true,
};

const hasPushOptIn = (item) => Boolean(item.push_notifications_opted_in || Number(item.push_notification_device_count || 0) > 0);

const audienceLabels = {
  all_artists: 'All artists',
  incomplete_artists: 'Artists needing profile work',
  unverified_artists: 'Unverified artists',
  all_collaborators: 'All collaborators',
  incomplete_collaborators: 'Collaborators needing profile work',
  artist: 'Selected artists',
  collaborator: 'Selected collaborators',
};

const profileCompletionTemplate = {
  id: 'profile_completion_reminder',
  name: 'Profile completion reminder',
  subject: 'Complete your KalaManch profile to get discovered',
  message: `Namaste,

Thank you for registering on KalaManch.art.

Your account has been created, but your public profile still needs important details before artists, collaborators, organizers, and competition reviewers can understand your work properly.

Please login and complete:
- Art category / organization type
- City / location
- Profile photo or logo
- Bio / about section
- Portfolio, video, website, Instagram, or YouTube links

If you joined for a competition, your profile details help KalaManch admin review your submission and showcase your work correctly.

Open KalaManch and complete your profile:
https://www.kalamanch.art

Har Talent Ko Manch,
KalaManch.art Team`,
  channels: ['email'],
};

const isIncompleteArtist = (artist) => analyzeArtistProfile(artist).score < 70;
const isIncompleteCollaborator = (collaborator) => analyzeCollaboratorProfile(collaborator).score < 70;

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export default function AdminBroadcasts() {
  const [form, setForm] = useState(initialForm);
  const [artists, setArtists] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [broadcastTemplates, setBroadcastTemplates] = useState([profileCompletionTemplate]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(profileCompletionTemplate.id);
  const [templateName, setTemplateName] = useState(profileCompletionTemplate.name);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [templateStatus, setTemplateStatus] = useState('');
  const [error, setError] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedBroadcastIds, setSelectedBroadcastIds] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);

  const selectedRole = form.audience.includes('artist') ? 'artist' : 'collaborator';
  const recipientOptions = selectedRole === 'artist' ? artists : collaborators;
  const isIncompleteAudience = form.audience === 'incomplete_artists' || form.audience === 'incomplete_collaborators';
  const isUnverifiedAudience = form.audience === 'unverified_artists';
  const isIncompleteAccount = (item) =>
    selectedRole === 'artist' ? isIncompleteArtist(item) : isIncompleteCollaborator(item);
  const filteredRecipientOptions = recipientOptions.filter((item) => {
    if (isIncompleteAudience && !isIncompleteAccount(item)) return false;
    if (isUnverifiedAudience && item.is_verified) return false;
    if (form.verified_only && !item.is_verified) return false;
    return true;
  });
  const selectableUsers = filteredRecipientOptions;
  const selectedAudienceCount = form.recipient_ids.length;

  const pushReadyCount = useMemo(
    () => filteredRecipientOptions.filter((item) => hasPushOptIn(item)).length,
    [filteredRecipientOptions],
  );
  const activeBroadcast = useMemo(
    () => broadcasts.find((broadcast) => ['queued', 'sending'].includes((broadcast.status || '').toLowerCase())) || null,
    [broadcasts],
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [artistsResponse, collaboratorsResponse, broadcastsResponse, settingsResponse] = await Promise.all([
        adminApi.get('/admin/artists'),
        adminApi.get('/admin/collaborators'),
        adminApi.get(`/admin/broadcasts${showDeleted ? '?include_deleted=true' : ''}`),
        adminApi.get('/admin/settings'),
      ]);
      setArtists(artistsResponse.data?.artists || []);
      setCollaborators(collaboratorsResponse.data?.collaborators || []);
      setBroadcasts(broadcastsResponse.data?.broadcasts || []);
      setSelectedBroadcastIds([]);
      const nextSettings = settingsResponse.data?.settings || null;
      const nextTemplates = nextSettings?.broadcast_templates?.templates?.length
        ? nextSettings.broadcast_templates.templates
        : [profileCompletionTemplate];
      setSettings(nextSettings);
      setBroadcastTemplates(nextTemplates);
      setSelectedTemplateId((current) => (
        nextTemplates.some((template) => template.id === current)
          ? current
          : nextTemplates[0]?.id || ''
      ));
      setTemplateName((current) => current || nextTemplates[0]?.name || '');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load broadcast data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  useEffect(() => {
    if (!activeBroadcast) return undefined;
    const intervalId = window.setInterval(() => {
      loadData();
    }, 2500);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBroadcast?.id, activeBroadcast?.status, showDeleted]);

  useEffect(() => {
    const nextIds = selectableUsers.map((item) => item.id).filter(Boolean);
    setForm((current) => {
      const currentIds = current.recipient_ids || [];
      const allStillValid = currentIds.length > 0 && currentIds.every((id) => nextIds.includes(id));
      if (allStillValid) {
        return current;
      }
      return { ...current, recipient_ids: nextIds };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.audience, form.verified_only, artists, collaborators]);

  const updateForm = (updates) => {
    setStatus('');
    setError('');
    setTemplateStatus('');
    setForm((current) => ({ ...current, ...updates }));
  };

  const toggleChannel = (channel) => {
    setForm((current) => {
      const nextChannels = current.channels.includes(channel)
        ? current.channels.filter((item) => item !== channel)
        : [...current.channels, channel];
      return { ...current, channels: nextChannels };
    });
  };

  const toggleRecipient = (id) => {
    setForm((current) => {
      const exists = current.recipient_ids.includes(id);
      return {
        ...current,
        recipient_ids: exists
          ? current.recipient_ids.filter((recipientId) => recipientId !== id)
          : [...current.recipient_ids, id],
      };
    });
  };

  const allSelectableRecipientsSelected =
    selectableUsers.length > 0 && selectableUsers.every((item) => form.recipient_ids.includes(item.id));

  const toggleAllSelectableRecipients = () => {
    setForm((current) => ({
      ...current,
      recipient_ids: allSelectableRecipientsSelected ? [] : selectableUsers.map((item) => item.id).filter(Boolean),
    }));
  };

  const handleAudienceChange = (audience) => {
    const incomplete = audience === 'incomplete_artists' || audience === 'incomplete_collaborators';
    const unverified = audience === 'unverified_artists';
    updateForm({
      audience,
      recipient_ids: [],
      verified_only: incomplete || unverified ? false : form.verified_only,
    });
  };

  const applyTemplateToForm = (template) => {
    if (!template) return;
    updateForm({
      subject: template.subject || '',
      message: template.message || '',
      channels: template.channels?.length ? template.channels : ['email'],
    });
    setSelectedTemplateId(template.id || '');
    setTemplateName(template.name || '');
  };

  const applyProfileCompletionTemplate = () => {
    const reminderTemplate = broadcastTemplates.find((template) => template.id === 'profile_completion_reminder') || profileCompletionTemplate;
    applyTemplateToForm(reminderTemplate);
  };

  const selectedTemplate = broadcastTemplates.find((template) => template.id === selectedTemplateId) || null;

  const slugifyTemplateId = (value) => value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `template_${Date.now()}`;

  const saveTemplates = async (nextTemplates, nextSelectedTemplateId) => {
    setTemplateSaving(true);
    setTemplateStatus('');
    setError('');
    try {
      const response = await adminApi.put('/admin/settings/broadcast-templates', {
        templates: nextTemplates.map((template) => ({
          id: template.id,
          name: template.name,
          subject: template.subject,
          message: template.message,
          channels: template.channels?.length ? template.channels : ['email'],
        })),
      });
      const savedTemplates = response.data?.settings?.templates?.length
        ? response.data.settings.templates
        : [profileCompletionTemplate];
      setBroadcastTemplates(savedTemplates);
      setSettings((current) => ({
        ...(current || {}),
        broadcast_templates: response.data?.settings || { templates: savedTemplates },
      }));
      const finalSelectedId = nextSelectedTemplateId && savedTemplates.some((template) => template.id === nextSelectedTemplateId)
        ? nextSelectedTemplateId
        : savedTemplates[0]?.id || '';
      setSelectedTemplateId(finalSelectedId);
      setTemplateName(savedTemplates.find((template) => template.id === finalSelectedId)?.name || '');
      setTemplateStatus('Template library saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save broadcast templates.');
    } finally {
      setTemplateSaving(false);
    }
  };

  const saveCurrentAsTemplate = async () => {
    const cleanName = templateName.trim();
    if (!cleanName) {
      setError('Template name is required.');
      return;
    }
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Add a subject and message before saving a template.');
      return;
    }
    const exactNameMatch = broadcastTemplates.find((template) => template.name.toLowerCase() === cleanName.toLowerCase()) || null;
    const targetTemplateId = exactNameMatch?.id
      || (selectedTemplate && selectedTemplate.name === cleanName ? selectedTemplate.id : '')
      || slugifyTemplateId(cleanName);

    const nextTemplate = {
      id: targetTemplateId,
      name: cleanName,
      subject: form.subject.trim(),
      message: form.message.trim(),
      channels: form.channels.length ? form.channels : ['email'],
    };
    const replacingExisting = broadcastTemplates.some((template) => template.id === targetTemplateId);
    let nextTemplates = [...broadcastTemplates];
    if (replacingExisting) {
      nextTemplates = nextTemplates.map((template) => (template.id === targetTemplateId ? { ...template, ...nextTemplate } : template));
    } else {
      nextTemplates.push(nextTemplate);
    }
    await saveTemplates(nextTemplates, nextTemplate.id);
  };

  const deleteSelectedTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Delete template "${selectedTemplate.name}"?`)) return;
    const nextTemplates = broadcastTemplates.filter((template) => template.id !== selectedTemplate.id);
    if (nextTemplates.length === 0) {
      setError('Keep at least one template in the library.');
      return;
    }
    await saveTemplates(nextTemplates, nextTemplates[0]?.id || '');
  };

  const sendBroadcast = async () => {
    setSending(true);
    setStatus('');
    setError('');
    try {
      const response = await adminApi.post('/admin/broadcasts', form);
      const result = response.data?.broadcast;
      setStatus(
        result?.progress_note
          ? `Broadcast queued. ${result.progress_note}`
          : `Broadcast queued for ${result?.recipient_count || 0} recipient(s).`,
      );
      setForm(initialForm);
      await loadData();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to send broadcast.');
    } finally {
      setSending(false);
    }
  };

  const canSend = form.channels.length > 0 && form.subject.trim() && form.message.trim() && selectedAudienceCount > 0 && !sending;
  const visibleBroadcasts = broadcasts.slice(0, 100);
  const allVisibleBroadcastsSelected = visibleBroadcasts.length > 0 && visibleBroadcasts.every((broadcast) => selectedBroadcastIds.includes(broadcast.id));

  const toggleBroadcastSelection = (id, checked) => {
    setSelectedBroadcastIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id)));
  };

  const toggleAllVisibleBroadcasts = () => {
    setSelectedBroadcastIds(allVisibleBroadcastsSelected ? [] : visibleBroadcasts.map((broadcast) => broadcast.id).filter(Boolean));
  };

  const runBroadcastAction = async (id, action) => {
    if (action === 'delete' && !window.confirm('Move this broadcast log to Deleted Broadcasts?')) return;
    if (action === 'permanent' && !window.confirm('Permanently delete this broadcast log? This cannot be undone.')) return;
    setHistoryBusy(true);
    try {
      if (action === 'restore') await adminApi.post(`/admin/broadcasts/${id}/restore`);
      else if (action === 'permanent') await adminApi.delete(`/admin/broadcasts/${id}/permanent`);
      else await adminApi.delete(`/admin/broadcasts/${id}`);
      await loadData();
    } finally {
      setHistoryBusy(false);
    }
  };

  const runBulkBroadcastAction = async (action) => {
    if (selectedBroadcastIds.length === 0) return;
    const confirmText =
      action === 'permanent_delete'
        ? `Permanently delete ${selectedBroadcastIds.length} selected broadcast log(s)? This cannot be undone.`
        : action === 'restore'
          ? `Undo delete for ${selectedBroadcastIds.length} selected broadcast log(s)?`
          : `Move ${selectedBroadcastIds.length} selected broadcast log(s) to Deleted Broadcasts?`;
    if (!window.confirm(confirmText)) return;
    setHistoryBusy(true);
    try {
      await adminApi.post('/admin/bulk/broadcasts', { ids: selectedBroadcastIds, action });
      await loadData();
    } finally {
      setHistoryBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Admin Broadcasts</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-black text-gray-950">Send email and app messages</h1>
            <p className="mt-2 max-w-3xl text-base leading-7 text-gray-600">
              Send official KalaManch updates to artists or collaborators. Use this for competition announcements,
              important platform updates, and launch messages.
            </p>
          </div>
          <button
            type="button"
            onClick={sendBroadcast}
            disabled={!canSend}
            className="rounded-full bg-[#FF6F00] px-6 py-3 font-bold text-white shadow-lg transition hover:bg-[#e96300] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sending ? 'Sending...' : 'Send broadcast'}
          </button>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Audience</p>
            <p className="mt-2 text-3xl font-black text-gray-950">{selectedAudienceCount}</p>
            <p className="mt-1 text-sm text-gray-600">recipient account(s)</p>
          </div>
          <div className="rounded-3xl border border-sky-100 bg-sky-50 p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-700">Push-ready</p>
            <p className="mt-2 text-3xl font-black text-gray-950">{pushReadyCount}</p>
            <p className="mt-1 text-sm text-gray-600">active app notification account(s)</p>
          </div>
          <div className="rounded-3xl border border-purple-100 bg-purple-50 p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">Email provider</p>
            <p className="mt-2 text-lg font-black text-gray-950">{settings?.resend_configured ? 'Ready' : 'Not configured'}</p>
            <p className="mt-1 text-sm text-gray-600">Resend email sending status</p>
          </div>
        </div>

        {activeBroadcast && (
          <div className="mt-5 rounded-3xl border border-orange-100 bg-orange-50 p-5">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-700">Broadcast queue</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-lg font-black text-gray-950">{activeBroadcast.subject}</p>
                <p className="text-sm font-semibold text-orange-700">
                  {activeBroadcast.progress_note || `Sending ${activeBroadcast.processed_count || 0} of ${activeBroadcast.recipient_count || 0}`}
                </p>
              </div>
              <span className="rounded-full bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-orange-700">
                {(activeBroadcast.status || 'queued').replace('_', ' ')}
              </span>
            </div>
          </div>
        )}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg">
          <h2 className="text-2xl font-black text-gray-950">Create broadcast</h2>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Audience</span>
              <select
                value={form.audience}
                onChange={(event) => handleAudienceChange(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-gray-900"
              >
                <option value="all_artists">All artists</option>
                <option value="incomplete_artists">Artists needing profile work</option>
                <option value="unverified_artists">Unverified artists</option>
                <option value="artist">Selected artists</option>
                <option value="all_collaborators">All collaborators</option>
                <option value="incomplete_collaborators">Collaborators needing profile work</option>
                <option value="collaborator">Selected collaborators</option>
              </select>
            </label>

            <div>
              <span className="text-sm font-bold text-gray-800">Message type</span>
              <div className="mt-2 flex flex-wrap gap-3">
                {['email', 'push'].map((channel) => (
                  <label key={channel} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-3 text-sm font-bold capitalize text-gray-800">
                    <input type="checkbox" checked={form.channels.includes(channel)} onChange={() => toggleChannel(channel)} />
                    {channel}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <label className="mt-5 inline-flex items-center gap-3 rounded-full border border-orange-100 bg-orange-50 px-4 py-2 text-sm font-bold text-gray-800">
            <input
              type="checkbox"
              checked={form.verified_only}
              onChange={(event) => updateForm({ verified_only: event.target.checked, recipient_ids: [] })}
            />
            Send only to verified registered users
          </label>
          {(isIncompleteAudience || isUnverifiedAudience) && (
            <p className="mt-2 text-sm leading-6 text-orange-700">
              {isUnverifiedAudience
                ? 'This audience shows artists who have not been verified yet, making it easier to remind new signups to complete their profile.'
                : 'Incomplete profile reminders include newly registered accounts by default. Turn on verified-only only if you want to exclude unverified competition signups.'}
            </p>
          )}

          <div className="mt-5 rounded-3xl border border-amber-100 bg-amber-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-700">Profile completion reminder</p>
                <p className="mt-1 text-sm leading-6 text-gray-700">
                  Load an editable email for artists or collaborators who registered but have not completed profile details.
                </p>
              </div>
              <button type="button" onClick={applyProfileCompletionTemplate} className="rounded-full border border-amber-200 bg-white px-4 py-2 text-sm font-bold text-amber-800 hover:bg-amber-100">
                Use reminder template
              </button>
            </div>
          </div>

          <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-slate-700">Template library</p>
                  <p className="mt-1 text-sm leading-6 text-gray-700">
                    Save reusable email or push templates here, load them into the composer, and update them any time.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTemplateId('');
                      setTemplateName('');
                      setTemplateStatus('');
                      setError('');
                    }}
                    disabled={templateSaving}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                  >
                    New template
                  </button>
                  <button
                    type="button"
                    onClick={saveCurrentAsTemplate}
                    disabled={templateSaving}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                  >
                    {templateSaving ? 'Saving...' : 'Save current as template'}
                  </button>
                  <button
                    type="button"
                    onClick={deleteSelectedTemplate}
                    disabled={templateSaving || !selectedTemplate}
                    className="rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-60"
                  >
                    Delete selected
                  </button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
                <label className="block">
                  <span className="text-sm font-bold text-gray-800">Saved templates</span>
                  <select
                    value={selectedTemplateId}
                    onChange={(event) => {
                      const nextTemplate = broadcastTemplates.find((template) => template.id === event.target.value);
                      setSelectedTemplateId(event.target.value);
                      setTemplateName(nextTemplate?.name || '');
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-gray-900"
                  >
                    {broadcastTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-bold text-gray-800">Template name</span>
                  <input
                    value={templateName}
                    onChange={(event) => {
                      setTemplateStatus('');
                      setError('');
                      setTemplateName(event.target.value);
                    }}
                    placeholder="For example: Competition reminder"
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-gray-900"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyTemplateToForm(selectedTemplate)}
                  disabled={!selectedTemplate}
                  className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                >
                  Load selected template
                </button>
                {selectedTemplate && (
                  <p className="self-center text-xs font-semibold text-gray-500">
                    Channels: {(selectedTemplate.channels || ['email']).join(', ')}
                  </p>
                )}
              </div>
              {templateStatus && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{templateStatus}</p>}
            </div>
          </div>

          <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold text-gray-900">Select recipients</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">
                  Showing {filteredRecipientOptions.length} matching account(s). Push OFF affects Android push only, not email delivery.
                </p>
              </div>
              <button type="button" onClick={toggleAllSelectableRecipients} className="w-fit rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-100">
                {allSelectableRecipientsSelected ? 'Clear shown' : 'Select all shown'}
              </button>
            </div>
            <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
              {selectableUsers.length === 0 && (
                <p className="rounded-2xl bg-white px-3 py-3 text-sm font-semibold text-gray-500 shadow-sm">
                  No matching recipients found for this audience.
                </p>
              )}
              {selectableUsers.map((item) => {
                const label = item.name || item.organization_name || item.email || 'Unnamed account';
                const profileScore = selectedRole === 'artist' ? analyzeArtistProfile(item).score : analyzeCollaboratorProfile(item).score;
                return (
                  <label key={item.id} className="flex items-start gap-3 rounded-2xl bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
                    <input
                      type="checkbox"
                      checked={form.recipient_ids.includes(item.id)}
                      onChange={() => toggleRecipient(item.id)}
                      className="mt-1"
                    />
                    <span>
                      <span className="block font-bold">{label}</span>
                      <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] ${item.is_verified ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {item.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {item.email || 'Email missing'} | {item.city || 'City not added'} | {hasPushOptIn(item) ? `Push ON (${item.push_notification_device_count || 1})` : 'Push OFF'}
                      </span>
                      <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-black ${profileScore >= 70 ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
                        Profile {profileScore}/100
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="mt-5 block">
            <span className="text-sm font-bold text-gray-800">Email subject / push title</span>
            <input
              value={form.subject}
              onChange={(event) => updateForm({ subject: event.target.value })}
              placeholder="For example: KalaManch Founding Painters Challenge is live"
              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-gray-900"
            />
          </label>

          <label className="mt-5 block">
            <span className="text-sm font-bold text-gray-800">Message</span>
            <textarea
              value={form.message}
              onChange={(event) => updateForm({ message: event.target.value })}
              rows="8"
              placeholder="Write the official message. Keep it useful, clear, and non-spammy."
              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm leading-6 text-gray-900"
            />
          </label>

          {status && <p className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{status}</p>}
          {error && <p className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</p>}
        </div>

        <aside className="space-y-6">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-[#FF6F00]">Preview</p>
            <h3 className="mt-3 text-2xl font-black text-gray-950">{form.subject || 'Broadcast title will appear here'}</h3>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-gray-700">
              {form.message || 'Write the broadcast message and preview it here before sending.'}
            </p>
            <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-xs leading-6 text-gray-600">
              <p><strong>Audience:</strong> {audienceLabels[form.audience]}</p>
              <p><strong>Selected recipients:</strong> {form.recipient_ids.length}</p>
              <p><strong>Channels:</strong> {form.channels.length ? form.channels.join(', ') : 'None selected'}</p>
              <p><strong>Safety:</strong> KalaManch identity and support note are added to email messages automatically.</p>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-purple-700">Broadcast history</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => setShowDeleted(false)} className={!showDeleted ? 'btn-primary px-4 py-2 text-sm' : 'btn-outline px-4 py-2 text-sm'}>
                Active
              </button>
              <button type="button" onClick={() => setShowDeleted(true)} className={showDeleted ? 'btn-primary px-4 py-2 text-sm' : 'btn-outline px-4 py-2 text-sm'}>
                Deleted
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-3">
              <button type="button" onClick={toggleAllVisibleBroadcasts} className="btn-outline px-3 py-1 text-xs" disabled={!visibleBroadcasts.length}>
                {allVisibleBroadcastsSelected ? 'Clear all visible' : 'Select all visible'}
              </button>
              {!showDeleted ? (
                <button
                  type="button"
                  onClick={() => runBulkBroadcastAction('delete')}
                  disabled={historyBusy || selectedBroadcastIds.length === 0}
                  className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Delete selected
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => runBulkBroadcastAction('restore')}
                    disabled={historyBusy || selectedBroadcastIds.length === 0}
                    className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    Undo selected
                  </button>
                  <button
                    type="button"
                    onClick={() => runBulkBroadcastAction('permanent_delete')}
                    disabled={historyBusy || selectedBroadcastIds.length === 0}
                    className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    Permanent delete selected
                  </button>
                </>
              )}
              <span className="text-xs font-semibold text-gray-500">{selectedBroadcastIds.length} selected</span>
            </div>
            <div className="mt-4 space-y-3">
              {loading && <p className="text-sm text-gray-500">Loading broadcasts...</p>}
              {!loading && broadcasts.length === 0 && <p className="text-sm text-gray-500">No broadcast messages sent yet.</p>}
              {visibleBroadcasts.map((broadcast) => (
                <div key={broadcast.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedBroadcastIds.includes(broadcast.id)}
                      onChange={(event) => toggleBroadcastSelection(broadcast.id, event.target.checked)}
                      className="mt-1 h-4 w-4 accent-orange-600"
                      aria-label="Select broadcast"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-950">{broadcast.subject}</p>
                      <p className="mt-1 text-xs text-gray-500">{formatDateTime(broadcast.created_at)} | {broadcast.audience_label}</p>
                      {!!broadcast.status && (
                        <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] text-orange-700">
                          {broadcast.status} {broadcast.progress_note ? `| ${broadcast.progress_note}` : ''}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-600">
                        Email {broadcast.email_sent || 0} sent, {broadcast.email_failed || 0} failed, {broadcast.email_skipped || 0} skipped | Push devices {broadcast.push_devices || 0}
                      </p>
                      {!!broadcast.email_failure_details?.length && (
                        <div className="mt-3 rounded-2xl border border-red-100 bg-red-50 p-3">
                          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-red-700">Email failures</p>
                          <div className="mt-2 space-y-1 text-xs leading-5 text-red-700">
                            {broadcast.email_failure_details.map((failure, index) => (
                              <p key={`${broadcast.id}-failure-${index}`}>
                                <strong>{failure.name || failure.email || 'Recipient'}:</strong> {failure.email || 'No email'} - {failure.reason || 'Unknown reason'}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      {!!broadcast.email_skipped_details?.length && (
                        <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 p-3">
                          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-700">Email skipped</p>
                          <div className="mt-2 space-y-1 text-xs leading-5 text-amber-700">
                            {broadcast.email_skipped_details.map((failure, index) => (
                              <p key={`${broadcast.id}-skipped-${index}`}>
                                <strong>{failure.name || failure.email || 'Recipient'}:</strong> {failure.email || 'No email'} - {failure.reason || 'Unknown reason'}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {showDeleted ? (
                          <>
                            <button type="button" onClick={() => runBroadcastAction(broadcast.id, 'restore')} className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-50">
                              Undo
                            </button>
                            <button type="button" onClick={() => runBroadcastAction(broadcast.id, 'permanent')} className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-50">
                              Permanent delete
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={() => runBroadcastAction(broadcast.id, 'delete')} className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50">
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
